const HISTORY_KEY = 'nacktColourway';
const WIDTHS = [200, 350, 450, 750, 850, 960, 1280, 1440, 1536, 1706, 1782, 1920, 2560, 2880, 3000];
const MAX_AGE = 5 * 60 * 1000;
export function sizePosition(options) {
  return options.findIndex(option => /size/i.test(typeof option === 'string' ? option : option.name));
}
export function resolveVariant(destination, sourceOptions, values, variantId) {
  if (variantId) return destination.variants.find(variant => String(variant.id) === String(variantId)) || null;
  const sourceSize = sizePosition(sourceOptions), targetSize = sizePosition(destination.options);
  if (sourceSize >= 0 && targetSize >= 0 && values[sourceSize]) {
    return destination.variants.find(variant => (variant.options?.[targetSize] || variant[`option${targetSize + 1}`]) === values[sourceSize]) || null;
  }
  return destination.variants.find(variant => variant.available) || destination.variants[0] || null;
}
export function productUrl(base, context, variant) {
  const url = new URL(base, context);
  const current = new URL(context);
  for (const key of ['preview_theme_id', '_fd', 'pb', 'country', 'currency', 'locale']) {
    if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
  }
  if (variant?.id) url.searchParams.set('variant', variant.id); else url.searchParams.delete('variant');
  return url;
}
export function compatibleFamily(envelope) {
  if (envelope.schema !== 1 || envelope.products.length < 2 || envelope.products.length > 6) return false;
  const reference = envelope.products[0];
  const names = reference.product.options;
  const sizes = reference.optionsWithValues.find(option => /size/i.test(option.name))?.values.map(String);
  if (names.length !== 2 || !sizes?.length || !names.some(name => /colou?r/i.test(name))) return false;
  return envelope.products.every(payload => {
    const options = payload.optionsWithValues;
    return payload.media.length > 0 && payload.media.length <= 6 &&
      JSON.stringify(payload.product.options) === JSON.stringify(names) &&
      options.every((option, index) => option.position === index + 1 &&
        (/colou?r/i.test(option.name) ? option.values.length === 1 :
          /size/i.test(option.name) && JSON.stringify(option.values.map(String)) === JSON.stringify(sizes))) &&
      options.length === 2 && payload.product.variants.every(variant => variant.options?.length === 2);
  });
}
export class FamilyCache {
  constructor(envelope, now = () => Date.now()) {
    this.now = now;
    this.context = [envelope.theme, envelope.schema, envelope.country, envelope.locale, envelope.currency].join(':');
    this.entries = new Map(envelope.products.map(payload => [this.key(payload.product.id), { payload, time: now() }]));
    this.pending = new Map();
  }
  key(id) { return `${this.context}:${id}`; }
  peek(id) { return this.entries.get(this.key(id))?.payload; }
  async get(id, loader) {
    const key = this.key(id), entry = this.entries.get(key);
    if (entry && this.now() - entry.time < MAX_AGE) return entry.payload;
    if (this.pending.has(key)) return this.pending.get(key);
    const pending = Promise.resolve().then(loader).then(payload => {
      if (String(payload.product.id) !== String(id)) throw new Error('Colourway identity mismatch');
      this.entries.set(key, { payload, time: this.now() });
      return payload;
    }).finally(() => this.pending.delete(key));
    this.pending.set(key, pending);
    return pending;
  }
}
export class ImageWarmer {
  constructor(load, { limit = 10, concurrency = 2, requestBudget = 5, byteBudget = 1500000 } = {}) {
    this.load = load; this.limit = limit; this.concurrency = concurrency;
    this.requestBudget = requestBudget; this.byteBudget = byteBudget;
    this.entries = new Map(); this.queue = []; this.active = 0; this.speculativeRequests = 0; this.speculativeBytes = 0; this.activeControllers = new Set(); this.disposed = false;
  }
  prepare(key, { intent = false } = {}) {
    if (this.disposed) return Promise.reject(new DOMException('Image warmer disposed', 'AbortError'));
    if (this.entries.has(key)) {
      const entry = this.entries.get(key);
      this.entries.delete(key); this.entries.set(key, entry);
      if (intent) {
        const index = this.queue.findIndex(job => job.key === key);
        if (index >= 0) this.queue.unshift(this.queue.splice(index, 1)[0]);
      }
      return entry.promise;
    }
    if (!intent && (this.speculativeRequests >= this.requestBudget || this.speculativeBytes + 250000 > this.byteBudget)) return Promise.resolve(null);
    if (!intent) { this.speculativeRequests++; this.speculativeBytes += 250000; }
    if (this.queue.length >= 32) return Promise.reject(new Error('Colourway image queue is full'));
    let resolve, reject;
    const promise = new Promise((yes, no) => { resolve = yes; reject = no; });
    this.entries.set(key, { promise });
    const job = { key, intent, resolve, reject };
    if (intent) this.queue.unshift(job); else this.queue.push(job);
    this.drain();
    return promise;
  }
  destroy() {
    this.disposed = true;
    this.queue.splice(0).forEach(job => job.resolve(null));
    this.activeControllers.forEach(controller => controller.abort());
    this.entries.clear();
  }
  drain() {
    while (!this.disposed && this.active < this.concurrency && this.queue.length) {
      const job = this.queue.shift(); this.active++;
      const controller = new AbortController(); this.activeControllers.add(controller);
      Promise.resolve().then(() => this.load(job.key, job.intent, controller.signal)).then(image => {
        const entry = this.entries.get(job.key);
        if (entry) entry.ready = true;
        job.resolve(image);
      }, error => { this.entries.delete(job.key); job.reject(error); }).finally(() => {
        this.active--; this.activeControllers.delete(controller);
        for (const [key, entry] of this.entries) {
          if (this.entries.size <= this.limit) break;
          if (entry.ready) this.entries.delete(key);
        }
        this.drain();
      });
    }
  }
}
function responsiveSource(media, width) {
  const candidate = WIDTHS.find(value => value >= Math.min(media.width, width)) || 3000;
  const url = new URL(media.src, location.href);
  url.searchParams.set('width', candidate);
  return url.href;
}
function sourceSet(media) {
  const widths = [...new Set([...WIDTHS.filter(width => width <= media.width), Math.min(media.width, 3000)])].sort((a,b) => a-b);
  return widths.map(width => { const url = new URL(media.src, location.href); url.searchParams.set('width', width); return `${url.href} ${width}w`; }).join(', ');
}
function loadImage(url, intent, signal) {
  return new Promise((resolve, reject) => {
    const image = new Image(); image.fetchPriority = intent ? 'high' : 'low'; image.decoding = 'async';
    const abort = () => { image.onload = null; image.onerror = null; image.removeAttribute('src'); reject(new DOMException('Image preparation cancelled', 'AbortError')); };
    signal?.addEventListener('abort', abort, { once: true });
    image.onload = () => image.decode().then(() => { signal?.removeEventListener('abort', abort); resolve(image); }, reject);
    image.onerror = () => { signal?.removeEventListener('abort', abort); reject(new Error('Colourway image failed to load')); };
    const descriptor = JSON.parse(url);
    image.sizes = descriptor.sizes;
    image.srcset = descriptor.srcset;
    image.src = descriptor.src;
  });
}
function frame() { return new Promise(resolve => requestAnimationFrame(resolve)); }
export function restockWidgetMatchesVariant(payload, variant, name, config) {
  if (!variant) return false;
  if (name === `${payload.product.title} - ${variant.title}`) return true;
  const baseSettings = config?.settings;
  const template = baseSettings?.configure_notify_me_enabled && config.backInStockTemplates?.find(value => value.product_variant_ids?.includes(variant.id));
  const settings = baseSettings && { ...baseSettings, ...template };
  if (name !== payload.product.title || !settings || settings.theme_config?.allowInvalidVariants || settings.show_button_on_preorder) return false;
  const eligible = payload.product.variants.filter(value => !value.available || config.enabledNotifyMeVariantIds?.includes(value.id));
  return eligible.length === 1 && eligible[0].id === variant.id;
}
function scrollSnapshot(anchor) {
  const viewport = window.visualViewport;
  return { y: window.scrollY, anchorY: anchor?.getBoundingClientRect().top, height: window.innerHeight,
    viewport: viewport && [viewport.width, viewport.height, viewport.offsetTop, viewport.offsetLeft, viewport.scale] };
}
export function colourwayScrollCorrection(before, after) {
  if (before.y <= 0 || before.anchorY == null || after.anchorY == null || Math.abs(after.y - before.y) > 1 ||
      before.height !== after.height || JSON.stringify(before.viewport) !== JSON.stringify(after.viewport)) return null;
  const delta = after.anchorY - before.anchorY;
  return Math.abs(delta) > 1 ? after.y + delta : null;
}
export class StoqAdapter {
  constructor(root, current, labels) {
    this.root = root; this.current = current; this.labels = labels; this.generation = 0; this.running = false; this.disposed = false;
    this.host = document.createElement('div'); this.host.dataset.colourwayRestock = ''; this.host.className = 'nackt-colourway-restock';
    const tray = root.querySelector('.nackt-mobile-sticky-purchase-tray');
    tray?.insertBefore(this.host, tray.querySelector('.add-to-cart-container'));
    this.controller = new AbortController();
    const signal = this.controller.signal;
    window.addEventListener('stoq:loaded', () => queueMicrotask(() => this.reconcile()), { signal });
    document.addEventListener('stoq:product-changed', () => queueMicrotask(() => this.reconcile()), { signal });
    this.observer = new MutationObserver(records => {
      const relevant = records.some(record => this.mountHost?.contains(record.target) || [...record.addedNodes].some(node => node.nodeType === 1 &&
        (node.id === 'restock-rocket-inline-container' || node.matches?.('[data-stoq-injected]') ||
          node.querySelector?.('#restock-rocket-inline-container, [data-stoq-injected]'))));
      if (!relevant || this.disposed) return;
      this.guard();
      if (this.guardFrame) return;
      this.guardFrame = requestAnimationFrame(() => {
        this.guardFrame = null;
        if (this.mountedGeneration !== this.target?.generation) this.reconcile();
      });
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
  }
  updateEntryButton() {
    if (this.entryButton) this.entryButton.disabled = !this.providerReady || !this.entryInput.checkValidity() || this.submitting;
  }
  createEntry() {
    const generation = this.generation;
    this.entry = document.createElement('div'); this.entry.className = 'nackt-colourway-restock__entry';
    const label = document.createElement('label'); label.className = 'sr-only'; label.textContent = this.labels.restockEmail;
    this.entryInput = document.createElement('input'); this.entryInput.className = 'nackt-colourway-restock__email';
    this.entryInput.id = `nackt-restock-email-${this.root.id}-${generation}`; label.htmlFor = this.entryInput.id;
    this.entryInput.type = 'email'; this.entryInput.autocomplete = 'email'; this.entryInput.required = true;
    this.entryInput.placeholder = this.labels.restockEmail;
    this.entryInput.value = window.nacktRestockContact?.getEmail() ?? '';
    this.entryButton = document.createElement('button'); this.entryButton.type = 'button'; this.entryButton.className = 'nackt-colourway-restock__button';
    this.entryButton.textContent = this.labels.restockRemind + ' \u2192'; this.entryButton.disabled = true;
    this.entry.append(label, this.entryInput, this.entryButton); this.host.append(this.entry);
    for (const name of ['input', 'change']) this.entryInput.addEventListener(name, () => {
      if (generation === this.generation && !this.disposed) {
        window.nacktRestockContact?.setEmail(this.entryInput.value);
        this.updateEntryButton();
      }
    });
    this.entryInput.addEventListener('compositionstart', () => { if (generation === this.generation) this.composing = true; });
    this.entryInput.addEventListener('compositionend', () => { if (generation === this.generation) { this.composing = false; this.guard(); } });
    this.entryInput.addEventListener('keydown', event => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      if (generation === this.generation && !event.isComposing && !this.composing) this.submitEntry();
    });
    this.entryButton.addEventListener('click', () => { if (generation === this.generation) this.submitEntry(); });
    this.entry.addEventListener('focusout', () => queueMicrotask(() => {
      if (generation === this.generation && !this.entry.contains(document.activeElement) && !this.submitting) this.guard();
    }));
    this.pendingStatus = document.createElement('span'); this.pendingStatus.setAttribute('role', 'status');
    this.pendingStatus.textContent = this.labels.restockLoading;
    this.recovery = document.createElement('div'); this.recovery.className = 'nackt-colourway-restock__recovery'; this.recovery.hidden = true;
    const message = document.createElement('p'); message.textContent = this.labels.restockError;
    const fallback = document.createElement('a'); fallback.href = this.target.url.href; fallback.textContent = this.labels.restockRetry;
    fallback.dataset.colourwayRestockFallback = '';
    this.recovery.append(message, fallback); this.host.append(this.pendingStatus, this.recovery);
    this.startFailureTimer();
  }
  startFailureTimer() {
    clearTimeout(this.failureTimer);
    const generation = this.generation;
    this.failureTimer = setTimeout(() => { if (!this.disposed && generation === this.generation && !this.providerReady) this.showRecovery(); }, 10000);
  }
  showRecovery() {
    if (this.recovery) this.recovery.hidden = false;
    if (this.pendingStatus) this.pendingStatus.hidden = true;
  }
  transferEmail(input) {
    if (!input || !this.entryInput) return;
    input.value = this.entryInput.value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
  presentNative(widget) {
    if (!this.providerReady || !this.mountHost?.contains(widget)) return;
    const input = widget.querySelector('#form-Email');
    if (!this.nativePresented) this.transferEmail(input);
    this.nativePresented = true;
    this.entryInput.disabled = true;
    this.mountHost.inert = false; this.mountHost.setAttribute('aria-hidden', 'false');
    this.mountHost.dataset.colourwayRestockReady = 'true'; this.host.dataset.colourwayRestockPresented = 'native';
  }
  additionalControls(widget) {
    return [...widget.querySelectorAll('input, select, textarea')].filter(control => control.id !== 'form-Email' &&
      !['hidden','submit','button'].includes(control.type) && !control.disabled);
  }
  async submitEntry() {
    if (this.disposed || this.submitting || this.entryButton?.disabled || !this.providerReady) return;
    const generation = this.generation, widget = this.mountHost?.querySelector('#restock-rocket-inline-container');
    if (!widget) return;
    const additional = this.additionalControls(widget);
    const fromInput = document.activeElement === this.entryInput;
    this.submitting = true; this.presentNative(widget);
    if (additional.length) {
      widget.classList.remove('nackt-restock-inline-container--compact');
      additional[0].focus({ preventScroll: true });
      this.submitting = false; return;
    }
    const focused = fromInput ? widget.querySelector('#form-Email') : widget.querySelector('#form-Button');
    focused?.focus({ preventScroll: true });
    try {
      await frame();
      if (this.disposed || generation !== this.generation) return;
      this.guard();
      if (!this.providerReady || !this.mountHost?.contains(widget)) return;
      const controls = this.additionalControls(widget);
      if (controls.length) { widget.classList.remove('nackt-restock-inline-container--compact'); controls[0].focus({ preventScroll: true }); return; }
      const button = widget.querySelector('#form-Button');
      if (button && !button.disabled) button.click();
    } finally { if (generation === this.generation) { this.submitting = false; this.updateEntryButton(); } }
  }
  sync(payload, variant, url, { defer = false } = {}) {
    // Capture only the current presented field, never a staged provider's cached contact.
    const currentInput = this.nativePresented ? this.providerInput : this.entryInput;
    if (currentInput) window.nacktRestockContact?.setEmail(currentInput.value);
    const settings = window._RestockRocketConfig?.settings;
    if (settings) {
      this.originalSelector ??= settings.inline_form_selector;
      settings.inline_form_selector = `#nackt-stoq-superseded-${this.root.id}`;
    }
    this.generation++;
    this.target = { payload, variant, url, generation: this.generation };
    const hadFocus = this.host.contains(document.activeElement);
    clearTimeout(this.failureTimer);
    this.mountHost?.remove(); this.mountHost = null;
    this.mountedGeneration = null; this.verifiedGeneration = null;
    this.seenGeneration = null; this.providerReady = false; this.nativePresented = false; this.providerInput = null;
    this.composing = false; this.submitting = false; this.remounts = 0;
    this.host.replaceChildren();
    delete this.host.dataset.colourwayRestockVerified;
    delete this.host.dataset.colourwayRestockPresented;
    if (hadFocus) this.root.querySelector(`[data-linked-product-id="${payload.product.id}"]`)?.focus({ preventScroll: true });
    this.host.hidden = !!variant?.available || !variant;
    this.root.querySelector('.nackt-mobile-sticky-purchase-tray')?.classList.toggle('nackt-mobile-sticky-purchase-tray--restock', !variant?.available && !!variant);
    this.entry = this.entryInput = this.entryButton = this.pendingStatus = this.recovery = null;
    if (!this.host.hidden) this.createEntry();
    this.guard();
    if (!defer) this.reconcile();
  }
  guard() {
    if (this.disposed || !this.target) return;
    const wasReady = this.providerReady;
    let currentWidget = null, ready = false;
    document.querySelectorAll('#restock-rocket-inline-container').forEach(widget => {
      if (!this.mountHost?.contains(widget)) { widget.remove(); return; }
      currentWidget = widget; this.seenGeneration = this.generation;
      widget.classList.add('nackt-restock-inline-container');
      if (!this.additionalControls(widget).length) widget.classList.add('nackt-restock-inline-container--compact');
      const name = widget.querySelector('#form-Product-Name')?.textContent || '';
      const target = this.target;
      const namedIdentity = restockWidgetMatchesVariant(target.payload, target.variant, name, window._RestockRocketConfig);
      const verifiedSuccess = this.verifiedGeneration === target.generation && !!widget.querySelector('#form-Success-Container');
      const verified = this.mountHost.dataset.productId === String(target.payload.product.id) &&
        this.mountHost.dataset.variantId === String(target.variant?.id) &&
        String(window._RestockRocketConfig?.product?.id) === String(target.payload.product.id) &&
        (namedIdentity && !!widget.querySelector('#form-Email') && !!widget.querySelector('#form-Button') || verifiedSuccess);
      if (verified) this.verifiedGeneration = target.generation;
      if (verified && !widget.querySelector('#form-Button .form-loading-spinner')) window.nacktEditorial?.prepareColourwayRestock?.(widget);
      ready = verified;
      const input = widget.querySelector('#form-Email');
      if (verified && input && input !== this.providerInput) {
        if (this.nativePresented) this.transferEmail(input);
        this.providerInput = input;
        const generation = this.generation;
        for (const name of ['input', 'change']) input.addEventListener(name, () => {
          if (generation === this.generation && !this.disposed && this.nativePresented && input === this.providerInput) {
            this.entryInput.value = input.value;
            window.nacktRestockContact?.setEmail(input.value);
          }
        });
      }
    });
    this.providerReady = ready;
    this.host.dataset.colourwayRestockVerified = ready ? 'true' : 'false';
    if (ready) {
      clearTimeout(this.failureTimer); this.pendingStatus.hidden = true; this.recovery.hidden = true;
      if (this.nativePresented || !this.composing && !this.entry.contains(document.activeElement)) this.presentNative(currentWidget);
    } else {
      if (wasReady) this.startFailureTimer();
      if (this.pendingStatus) this.pendingStatus.hidden = !this.recovery.hidden;
      if (this.nativePresented && this.providerInput) this.entryInput.value = this.providerInput.value;
      this.nativePresented = false; delete this.host.dataset.colourwayRestockPresented;
      if (this.entryInput) this.entryInput.disabled = false;
      if (this.mountHost) {
        this.mountHost.inert = true; this.mountHost.setAttribute('aria-hidden', 'true');
        delete this.mountHost.dataset.colourwayRestockReady;
      }
      if (!currentWidget && this.seenGeneration === this.generation && this.remounts < 2) {
        this.seenGeneration = null; this.mountedGeneration = null; this.remounts++; this.startFailureTimer();
      }
    }
    this.updateEntryButton();
    this.root.querySelectorAll('[name="selling_plan"], [data-stoq-injected]').forEach(element => element.remove());
  }
  async reconcile() {
    if (this.running || this.disposed || !this.target) return;
    const app = window._RestockRocket, config = window._RestockRocketConfig;
    if (!app?.openInlineForm || !app?.updateProductContext || !config?.settings) return;
    this.running = true;
    try {
      let target;
      do {
        target = this.target;
        if (String(config.product?.id) !== String(target.payload.product.id)) {
          if (config.isUpdatingProductContext) break;
          const updated = await app.updateProductContext(target.payload.product.handle);
          if (!updated || String(config.product?.id) !== String(target.payload.product.id)) break;
        }
        if (target !== this.target || this.disposed) continue;
        if (this.mountedGeneration === target.generation) break;
        this.mountHost?.remove(); this.mountHost = null;
        const originalSelector = config.settings.inline_form_selector;
        const inertSelector = `#nackt-stoq-superseded-${this.root.id}`;
        config.settings.inline_form_selector = inertSelector;
        this.originalSelector ??= originalSelector;
        this.guard();
        if (!target.variant || target.variant.available) { this.mountedGeneration = target.generation; break; }
        const host = document.createElement('div');
        host.className = 'nackt-colourway-restock__mount'; host.setAttribute('aria-hidden', 'true');
        host.id = `nackt-stoq-${this.root.id}-${target.generation}`;
        host.dataset.productId = target.payload.product.id; host.dataset.variantId = target.variant.id; host.inert = true;
        this.mountHost = host;
        this.host.prepend(host);
        this.mountedGeneration = target.generation;
        app.openInlineForm(target.payload.product, target.variant.id, { email: this.entryInput.value }, `#${host.id}`, 'beforeend');
      } while (target !== this.target);
    } catch {} finally { this.running = false; this.guard(); }
  }
  destroy() {
    this.disposed = true; this.generation++; clearTimeout(this.failureTimer); this.observer.disconnect(); cancelAnimationFrame(this.guardFrame); this.controller.abort(); this.host.remove();
    const settings = window._RestockRocketConfig?.settings;
    if (settings && this.originalSelector != null) settings.inline_form_selector = this.originalSelector;
  }
}
export class ColourwayController {
  constructor(root, component, envelope) {
    if (!compatibleFamily(envelope) || String(envelope.theme) !== String(window.Shopify.theme.id)) throw new Error('Unsupported colourway contract');
    this.root = root; this.component = component; this.envelope = envelope;
    this.cache = new FamilyCache(envelope); this.warmer = new ImageWarmer(loadImage);
    this.native = new Map([...root.querySelectorAll('template[data-colourway-native]')].map(template => [Number(template.dataset.colourwayNative), template]));
    this.twoPerRow = component.$refs.mediaList.classList.contains('lg:grid-cols-2');
    this.halfMediaSizes = component.$refs.mediaList.querySelectorAll('image-with-placeholder > img')[1]?.sizes || envelope.mediaSizes;
    this.generation = 0; this.controller = new AbortController(); this.disposed = false; this.dragging = false;
    this.productTitleSuffix = document.title.replace(component.product.title, '');
    root.nacktColourwayController = this;
    root.dataset.colourwayController = 'stable';
    component._colourwayPayload = this.cache.peek(component.product.id);
    component.refreshColourwayDisplay();
    this.stoq = new StoqAdapter(root, () => this.component, envelope.labels);
    this.badgeObserver = new MutationObserver(records => {
      for (const record of records) for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        const badges = [node.closest('[data-lably-placement]'), ...node.querySelectorAll('[data-lably-placement]')];
        for (const badge of badges) if (badge && badge.dataset.currentProductVariant &&
          badge.dataset.currentProductVariant !== `gid://shopify/ProductVariant/${this.component.currentVariantId}`) badge.remove();
      }
    });
    this.badgeObserver.observe(component.productMediaContainerEl, { childList: true, subtree: true });
    const signal = this.controller.signal;
    root.addEventListener('click', event => this.onClick(event), { signal });
    for (const event of ['pointerover', 'focusin', 'pointerdown']) root.addEventListener(event, e => this.onIntent(e), { signal, passive: true });
    root.addEventListener('pointerdown', e => { if (e.target.closest('.splide__track')) this.dragging = true; }, { signal, passive: true });
    for (const event of ['pointerup', 'pointercancel']) window.addEventListener(event, () => { this.dragging = false; this.release?.(); this.release = null; }, { signal, passive: true });
    window.addEventListener('popstate', () => this.restore(), { signal });
    window.addEventListener('pageshow', event => { if (event.persisted) this.restore(); }, { signal });
    document.addEventListener('theme:variant:change', event => {
      if (event.detail.productRootEl !== root || component._colourwayTransaction && !event.detail.colourwayCommit) return;
      this.refreshSwatches();
      if (event.detail.colourwayCommit) this.stoq.reconcile();
      else this.stoq.sync(this.cache.peek(component.product.id), component.currentVariant, new URL(location.href));
      if (!this.applying) this.refreshBadge();
    }, { signal });
    document.addEventListener('shopify:section:unload', event => { if (event.target.contains(root)) this.destroy(); }, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.revalidateCurrent();
    }, { signal });
    this.saveHistory('replace'); this.refreshSwatches();
    this.stoq.sync(component._colourwayPayload, component.currentVariant, new URL(location.href));
    this.startWarm();
  }
  values() { return [this.component.option1, this.component.option2, this.component.option3]; }
  variant(payload, id) { return resolveVariant(payload.product, this.component.product.options, this.values(), id); }
  url(payload, variant) { return productUrl(payload.url, location.href, variant); }
  refreshSwatches() {
    const selectedSize = this.values()[sizePosition(this.component.product.options)];
    const showAlternatives = this.component.currentVariant?.available === false && selectedSize != null && selectedSize !== '';
    for (const link of this.root.querySelectorAll('[data-linked-product-id]')) {
      const payload = this.cache.peek(Number(link.dataset.linkedProductId));
      if (!payload) continue;
      const variant = this.variant(payload);
      link.href = this.url(payload, variant).href;
      const active = Number(link.dataset.linkedProductId) === this.component.product.id;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current');
      const showAvailability = showAlternatives && sizePosition(payload.product.options) >= 0;
      const unavailable = showAvailability && variant?.available !== true;
      link.toggleAttribute('data-colourway-size-unavailable', unavailable);
      if (showAvailability) {
        const label = `${payload.colour}, ${unavailable ? this.envelope.labels.soldOut : this.envelope.labels.available}`;
        link.setAttribute('aria-label', label);
        link.setAttribute('title', label);
      } else {
        link.removeAttribute('aria-label');
        link.removeAttribute('title');
      }
    }
  }
  mediaIndex(payload) { return Math.max(0, Math.min(this.component.splideIndex || 0, payload.media.length - 1)); }
  sizesFor(payload, index) {
    if (!this.twoPerRow) return this.envelope.mediaSizes;
    const firstFull = this.component.firstMediaFullWidth || payload.media.length % 2 === 1;
    const lastFull = this.component.firstMediaFullWidth && payload.media.length % 2 === 0;
    return index === 0 && firstFull || index === payload.media.length - 1 && lastFull ? this.envelope.mediaSizes : this.halfMediaSizes;
  }
  visibleIndices(payload) {
    const active = this.mediaIndex(payload);
    if (matchMedia('(max-width: 1023px)').matches) return [active];
    const list = this.component.$refs.mediaList;
    const slots = [...list.children];
    const indices = slots.flatMap((slot, index) => {
      const rect = slot.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < innerHeight && rect.width > 0 ? [Math.min(index, payload.media.length - 1)] : [];
    });
    const rect = list.getBoundingClientRect(), gap = parseFloat(getComputedStyle(list).rowGap) || 0;
    const columns = this.twoPerRow ? 2 : 1;
    let y = rect.top, row = [], rowHeight = 0;
    const flush = () => {
      if (y + rowHeight > 0 && y < innerHeight) indices.push(...row);
      y += rowHeight + gap; row = []; rowHeight = 0;
    };
    payload.media.forEach((media, index) => {
      const full = columns === 1 || this.sizesFor(payload, index) === this.envelope.mediaSizes;
      if (full && row.length) flush();
      const width = full ? rect.width : rect.width / columns;
      const height = index === 4 ? width * media.height / media.width : width;
      row.push(index); rowHeight = Math.max(rowHeight, height + 1);
      if (full || row.length === columns) flush();
    });
    if (row.length) flush();
    return [...new Set(indices.length ? indices : [active])];
  }
  imageRequest(payload, index) {
    const media = payload.media[index];
    return JSON.stringify({ src: media.src, srcset: sourceSet(media), sizes: this.sizesFor(payload, index), viewport: innerWidth, dpr: devicePixelRatio });
  }
  prepareMedia(payload, intent, indices = this.visibleIndices(payload)) {
    return Promise.all(indices.map(index => this.warmer.prepare(this.imageRequest(payload, index), { intent })));
  }
  async startWarm() {
    const hero = this.root.querySelector('[data-product-featured-image], [data-product-single-media-wrapper] image-with-placeholder > img');
    try { await hero?.decode(); } catch { return; }
    await frame();
    const connection = navigator.connection;
    if (connection?.saveData || /^(slow-)?2g$/.test(connection?.effectiveType || '')) return;
    const warm = () => {
      if (this.disposed) return;
      for (const payload of this.envelope.products) {
        if (payload.product.id !== this.component.product.id) this.prepareMedia(payload, false, [this.mediaIndex(payload)]).catch(() => {});
      }
    };
    this.idle = window.requestIdleCallback ? requestIdleCallback(warm, { timeout: 1500 }) : setTimeout(warm, 300);
  }
  async loadPayload(id) {
    return this.cache.get(id, async () => {
      const old = this.cache.peek(id), url = new URL(old.url, location.href);
      url.searchParams.set('section_id', this.envelope.section);
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Colourway state unavailable');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const state = JSON.parse(document.querySelector('[data-colourway-state]').textContent);
      if ([state.theme, state.schema, state.country, state.locale, state.currency].join(':') !== this.cache.context) throw new Error('Colourway market changed');
      const payload = state.products.find(member => Number(member.product.id) === Number(id));
      const native = document.querySelector(`template[data-colourway-native="${id}"]`);
      if (!payload || !native) throw new Error('Colourway fragment unavailable');
      if (!compatibleFamily(state) || !compatibleFamily({ ...state, products: [this.envelope.products[0], payload] })) throw new Error('Colourway options changed');
      this.native.set(Number(id), native);
      return payload;
    });
  }
  onIntent(event) {
    const link = event.target.closest('[data-linked-product-id]');
    if (!link || !this.root.contains(link)) return;
    const id = Number(link.dataset.linkedProductId);
    if (this.cache.peek(id)) this.loadPayload(id).then(payload => this.prepareMedia(payload, true)).catch(() => {});
  }
  onClick(event) {
    const link = event.target.closest('[data-linked-product-id]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey || link.target === '_blank') return;
    const id = Number(link.dataset.linkedProductId);
    if (!this.cache.peek(id)) return;
    if (link.hasAttribute('data-colourway-failed')) return;
    event.preventDefault();
    if (id === this.component.product.id) { this.generation++; this.clearPending(); return; }
    this.switchTo(id, { link, history: 'push' }).catch(() => {});
  }
  async switchTo(id, navigation = {}) {
    const generation = ++this.generation, start = performance.now();
    const current = () => !this.disposed && generation === this.generation;
    const diagnostics = { generation, productId: id, intent: start };
    performance.mark(`nackt:colourway:${generation}:intent`);
    this.clearPending();
    this.pendingTimer = setTimeout(() => {
      if (!current()) return;
      this.root.setAttribute('aria-busy', 'true');
      navigation.link?.setAttribute('data-colourway-pending', '');
    }, 120);
    try {
      const payload = await this.loadPayload(id);
      diagnostics.payloadReady = performance.now();
      let variant = this.variant(payload, navigation.variantId);
      const targetIndices = () => {
        const indices = this.visibleIndices(payload);
        if (navigation.mediaIndex != null) indices.push(Math.max(0, Math.min(navigation.mediaIndex, payload.media.length - 1)));
        return [...new Set(indices)];
      };
      await this.prepareMedia(payload, true, targetIndices());
      if (this.dragging) await new Promise(resolve => { this.release?.(); this.release = resolve; });
      if (!current()) return false;
      await this.prepareMedia(payload, true, targetIndices());
      diagnostics.mediaDecoded = performance.now();
      if (!current()) return false;
      const index = navigation.mediaIndex == null ? this.mediaIndex(payload) : Math.min(navigation.mediaIndex, payload.media.length - 1);
      // Size may have changed while a cold image was being prepared.
      variant = this.variant(payload, navigation.variantId);
      await frame();
      if (!current()) return false;
      const url = navigation.url || this.url(payload, variant);
      const anchor = this.root.querySelector('[data-linked-colourways-root]');
      const beforeScroll = scrollSnapshot(anchor);
      if (navigation.history === 'push') this.saveHistory('replace');
      this.applying = true;
      const applied = await this.component.applyColourway(payload, { variant, mediaIndex: index }, {
        isCurrent: current,
        commit: component => {
          this.patchContent(payload, variant);
          this.patchGallery(payload, index);
          this.patchNative(payload);
          this.patchMetadata(payload, url);
          this.refreshSwatches();
          this.stoq?.sync(payload, variant, url, { defer: true });
        },
        settle: () => {
          if (navigation.history !== 'none') this.saveHistory(navigation.history || 'push', url);
        }
      });
      if (!applied || this.disposed) return false;
      const correction = colourwayScrollCorrection(beforeScroll, scrollSnapshot(anchor));
      if (correction != null) scrollTo({ top: correction, behavior: 'instant' });
      this.applying = false;
      this.refreshBadge();
      window.nacktEditorial?.refreshColourwayPresentation?.(this.root);
      diagnostics.commit = performance.now(); diagnostics.duration = diagnostics.commit - start;
      diagnostics.preparation = diagnostics.mediaDecoded - start;
      performance.mark(`nackt:colourway:${generation}:commit`);
      this.lastCommit = diagnostics;
      const commitId = `${this.envelope.theme}:${Date.now()}:${generation}`;
      try {
      const published = window.Shopify.analytics?.publish?.('nackt_colourway_viewed', {
        commit_id: commitId,
        product_id: payload.product.id, product_title: payload.product.title,
        variant_id: variant?.id || null, variant_title: variant?.title || null, sku: variant?.sku || null,
        currency: this.envelope.currency, value: variant ? variant.price / 100 : null, url: url.href
      });
      Promise.resolve(published).catch(() => {});
      } catch {}
      this.root.dispatchEvent(new CustomEvent('nackt:colourway:commit' , { bubbles: true, detail: {
        product: payload.product, variant, url: url.href, currency: this.envelope.currency,
        commitId, diagnostics
      } }));
      const size = sizePosition(payload.product.options);
      this.root.querySelector('[data-colourway-announcement]').textContent = [payload.colour,
        variant ? variant.options[size] : this.envelope.labels.chooseSize,
        variant?.available ? this.envelope.labels.available : this.envelope.labels.soldOut].filter(Boolean).join(', ');
      return true;
    } catch (error) {
      if (current()) {
        this.root.querySelector('[data-colourway-announcement]').textContent = this.envelope.labels.error;
        if (navigation.link) navigation.link.dataset.colourwayFailed = '';
      }
      return false;
    } finally {
      this.applying = false;
      if (current()) this.clearPending();
    }
  }
  clearPending() {
    clearTimeout(this.pendingTimer); this.root.removeAttribute('aria-busy');
    this.root.querySelectorAll('[data-colourway-pending]').forEach(link => link.removeAttribute('data-colourway-pending'));
  }
  patchContent(payload, variant) {
    this.root.dataset.productUrlPath = payload.url;
    this.root.dataset.productOptionNames = JSON.stringify(payload.product.options);
    this.root.querySelectorAll('[data-product-title]').forEach(element => { element.textContent = payload.product.title; });
    this.root.querySelectorAll('.product-header-block__colour, .nackt-linked-colourways__current').forEach(element => { element.textContent = payload.colour; });
    this.root.querySelector('.nackt-product-form__block--description .rte')?.replaceChildren(document.createRange().createContextualFragment(payload.description));
    const form = this.root.querySelector('form.shopify-product-form');
    form.querySelectorAll('[name="product-id"], [name="product_id"]').forEach(input => { input.value = payload.product.id; });
    form.dataset.productId = payload.product.id;
    const select = form.querySelector('select[name="id"]');
    if (select) {
      select.replaceChildren(...payload.product.variants.map(value => {
        const option = document.createElement('option'); option.value = value.id; option.textContent = value.title;
        option.disabled = !value.available; option.selected = value.id === variant?.id; return option;
      }));
      select.disabled = true;
    }
    form.querySelectorAll('input[name="id"]').forEach(input => { input.value = variant?.id || ''; input.disabled = !variant; });
  }
  patchNative(payload) {
    const form = this.root.querySelector('form.shopify-product-form');
    const template = this.native.get(payload.product.id);
    for (const marker of ['data-colourway-payment-terms', 'data-colourway-payment-button']) {
      const old = form.querySelector(`[${marker}]`), fresh = template?.content.querySelector(`[${marker}]`);
      if (old && fresh) old.replaceWith(fresh.cloneNode(true));
    }
    window.Shopify.PaymentButton?.init?.();
  }
  patchGallery(payload, index) {
    const component = this.component, list = component.$refs.mediaList;
    const splide = component.splide;
    const mounted = component.isUsingSlideshowToDisplayMedia;
    if (mounted) { splide.Components.Transition.cancel(); splide.Components.NacktPositionDots?.cancel(); }
    if (component.mediaListIsReordered) component.resetMediaListOrder();
    component.mediaListIsReordered = false;
    list.classList.toggle('[&>li:first-child]:col-span-2', this.twoPerRow && (component.firstMediaFullWidth || payload.media.length % 2 === 1));
    list.classList.toggle('[&>li:last-child]:col-span-2', this.twoPerRow && component.firstMediaFullWidth && payload.media.length % 2 === 0);
    list.querySelectorAll('[data-lably-placement]').forEach(badge => badge.remove());
    let slots = [...list.children].filter(child => child.classList.contains('splide__slide'));
    const countChanged = slots.length !== payload.media.length;
    const visible = new Set(this.visibleIndices(payload));
    while (slots.length < payload.media.length) {
      const slot = slots[0].cloneNode(true);
      slot.querySelectorAll('[data-lably-placement]').forEach(badge => badge.remove());
      slot.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
      slot.removeAttribute('id'); list.append(slot); slots.push(slot);
    }
    while (slots.length > payload.media.length) slots.pop().remove();
    slots.forEach((slot, position) => {
      const media = payload.media[position], wrapper = slot.querySelector('[data-product-single-media-wrapper]'), image = wrapper.querySelector('[data-product-featured-image], image-with-placeholder > img');
      slot.classList.toggle('max-lg:[.splide:not(.is-active)_&]:hidden', position > 0);
      slot.dataset.index = position; slot.dataset.order = position + 1; slot.removeAttribute('data-media-group-id');
      wrapper.dataset.mediaId = media.id; wrapper.dataset.productImageIndex = position;
      if (media.contextual) wrapper.setAttribute('data-nackt-contextual-product-image', 'true');
      else wrapper.removeAttribute('data-nackt-contextual-product-image');
      image.alt = media.alt; image.width = media.width; image.height = media.height;
      image.sizes = this.sizesFor(payload, position); image.srcset = sourceSet(media); image.src = media.src;
      image.loading = visible.has(position) ? 'eager' : 'lazy';
      image.fetchPriority = 'auto'; image.classList.add('is-complete');
    });
    component.allSlides = slots; component.allHorizontalThumbnailsSlides = []; component.allVerticalThumbnailsSlides = [];
    component._colourwayMediaIndex = index; component.splideIndex = index; component.slideCount = slots.length;
    if (mounted) {
      splide.Components.Controller.setIndex(index);
      if (countChanged) splide.refresh();
      splide.Components.Move.jump(index);
      splide.emit('slides:updated');
      component.measureMediaIndicator();
    }
  }
  patchMetadata(payload, url) {
    document.title = payload.product.title + this.productTitleSuffix;
    const canonical = new URL(payload.url, location.href).href;
    const socialImage = new URL(payload.media[0].src, location.href); socialImage.searchParams.delete('width');
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonical);
    const values = { 'og:title': document.title, 'twitter:title': document.title, 'og:url': canonical,
      'og:description': payload.metaDescription, 'twitter:description': payload.metaDescription,
      'og:image': socialImage.href, 'og:image:secure_url': socialImage.href,
      'og:image:width': String(payload.media[0].width), 'og:image:height': String(payload.media[0].height),
      'og:price:amount': String(payload.product.price / 100), 'og:price:currency': this.envelope.currency,
      'twitter:image': socialImage.href };
    for (const [key, value] of Object.entries(values)) document.querySelector(`meta[property="${key}"], meta[name="${key}"]`)?.setAttribute('content', value);
    document.querySelector('meta[name="description"]')?.setAttribute('content', payload.metaDescription);
    const jsonld = document.querySelector(`[data-colourway-jsonld="${this.envelope.section}"]`);
    if (jsonld) jsonld.textContent = payload.jsonld;
    const zoom = document.querySelector(`[data-colourway-zoom="${this.envelope.section}"]`);
    if (zoom) window.Alpine.$data(zoom).productUrl = payload.url;
    if (window.currentPageProduct) window.currentPageProduct = { ...window.currentPageProduct, ...payload.product };
  }
  refreshBadge() {
    window.dispatchEvent(new CustomEvent('lably:check', { detail: { productId: this.component.product.id, variantId: this.component.currentVariantId } }));
  }
  saveHistory(mode, url = new URL(location.href)) {
    const state = { ...history.state, [HISTORY_KEY]: { schema: 1, productId: this.component.product.id,
      variantId: this.component.currentVariantId, mediaIndex: this.component.splideIndex, scrollY: window.scrollY } };
    history[mode === 'push' ? 'pushState' : 'replaceState'](state, '', url.href);
  }
  async restore() {
    const payload = this.envelope.products.find(member => new URL(member.url, location.href).pathname === location.pathname);
    if (!payload) { location.reload(); return; }
    const state = history.state?.[HISTORY_KEY], variantId = new URL(location.href).searchParams.get('variant');
    if (payload.product.id === this.component.product.id) {
      this.generation++; this.clearPending();
      const variant = this.variant(payload, variantId);
      if (variant) {
        this.component.shouldUpdateHistoryState = false; this.component.currentVariant = variant;
        await this.component.$nextTick(); this.component.shouldUpdateHistoryState = true;
        this.root.querySelectorAll('input[data-single-option-selector]').forEach(input => { input.checked = input.value === this.component[`option${input.dataset.position}`]; });
      }
      return;
    }
    await this.switchTo(payload.product.id, { history: 'none', url: new URL(location.href), variantId, mediaIndex: state?.mediaIndex });
  }
  async revalidateCurrent() {
    const id = this.component.product.id, generation = this.generation;
    try {
      const payload = await this.loadPayload(id);
      if (generation !== this.generation || id !== this.component.product.id || this.disposed) return;
      this.component._colourwayPayload = payload;
      this.component.product.variants = payload.product.variants;
      this.component._soldOutOrUnavailableVariants = null;
      this.component.refreshColourwayDisplay(); this.component.updateOptionsAvailabilities();
      this.refreshSwatches();
    } catch {}
  }
  destroy() {
    this.disposed = true; this.generation++; this.controller.abort(); this.clearPending(); this.release?.(); this.stoq.destroy(); this.warmer.destroy(); this.badgeObserver.disconnect();
    if (window.cancelIdleCallback) cancelIdleCallback(this.idle); else clearTimeout(this.idle);
    delete this.root.nacktColourwayController;
  }
}
function initialize(root) {
  if (!root || root.nacktColourwayController || root._colourwayInitializing || !root.querySelector('[data-colourway-state]')) return;
  const component = window.Alpine?.$data(root);
  if (!component?.productRoot) return;
  root._colourwayInitializing = true;
  component.$nextTick(() => {
    delete root._colourwayInitializing;
    if (!root.isConnected || root.nacktColourwayController || component._disposed) return;
    try { new ColourwayController(root, component, JSON.parse(root.querySelector('[data-colourway-state]').textContent)); }
    catch (error) { console.error('Colourway controller:', error); }
  });
}
if (typeof window !== 'undefined') {
  window.nacktColourways = {
    resolveSwatchUrl(link, names, ...values) {
      const controller = link.closest('[data-product-root]')?.nacktColourwayController;
      const payload = controller?.cache.peek(Number(link.dataset.linkedProductId));
      return payload ? controller.url(payload, resolveVariant(payload.product, names, values)).href : window.nacktEditorial.resolveLinkedSwatchUrl(link, names, ...values);
    }
  };
  document.addEventListener('nackt:product-ready', event => initialize(event.target));
  document.addEventListener('shopify:section:load', event => initialize(event.target.querySelector('[data-product-root]')));
  initialize(document.querySelector('[data-product-root]'));
}
